
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, CalculatorIcon, Percent, EuroIcon } from 'lucide-react';

const formSchema = z.object({
  afspraken: z.string().min(1, "Vul het gewenste aantal afspraken in"),
  doelgroep: z.string().min(1, "Selecteer een doelgroep"),
  regio: z.string().min(1, "Selecteer een regio"),
  crmIntegratie: z.string().min(1, "Maak een keuze"),
  contactpersonen: z.string().min(1, "Maak een keuze"),
  taal: z.string().min(1, "Selecteer een taal"),
  dmuComplexiteit: z.string().min(1, "Selecteer de complexiteit"),
  productComplexiteit: z.string().min(1, "Selecteer de complexiteit"),
  leadsType: z.string().min(1, "Selecteer het type leads"),
  marktgrootte: z.string().min(1, "Vul de marktgrootte in"),
  conversiePercentage: z.string().min(1, "Vul het conversiepercentage in"),
  campagneDuur: z.string().min(1, "Vul de campagneduur in"),
  offertePercentage: z.string().min(1, "Vul het offertepercentage in"),
  dealValue: z.string().min(1, "Vul de gemiddelde dealwaarde in"),
  closingPercentage: z.string().min(1, "Vul het closing percentage in"),
  naam: z.string().optional(),
  bedrijf: z.string().optional(),
  email: z.string().email("Voer een geldig e-mailadres in").optional(),
  telefoonnummer: z.string().optional(),
});

export type CalculatorFormValues = z.infer<typeof formSchema>;

interface CalculatorFormProps {
  onSubmit: (values: CalculatorFormValues) => void;
  loading: boolean;
}

const CalculatorForm: React.FC<CalculatorFormProps> = ({ onSubmit, loading }) => {
  const form = useForm<CalculatorFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      afspraken: "10",
      doelgroep: "b2b-diensten",
      regio: "vlaanderen",
      crmIntegratie: "nee",
      contactpersonen: "een",
      taal: "nederlands",
      dmuComplexiteit: "eenvoudig",
      productComplexiteit: "laag",
      leadsType: "medium",
      marktgrootte: "5000",
      conversiePercentage: "10", 
      campagneDuur: "6",
      offertePercentage: "80",
      dealValue: "2500",
      closingPercentage: "25",
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="grid gap-6">
        <div className="mb-6 text-sm text-gray-500 bg-gray-50 border-l-4 border-brand-red/30 p-4 rounded">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              Deze calculator geeft enkel een indicatieve prijsindicatie. Voor een nauwkeurige berekening die rekening houdt met uw specifieke situatie, raden wij aan om een persoonlijk gesprek in te plannen met een van onze experts.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="grid gap-3">
            <label htmlFor="afspraken" className="font-medium text-gray-700 h-7 flex items-center">
              Aantal gewenste afspraken per maand*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  Hoeveel nieuwe salesafspraken wilt u maandelijks ontvangen? Dit bepaalt uw maandbudget.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="relative h-12">
              <input
                id="afspraken"
                {...form.register("afspraken")}
                type="number"
                min="5"
                max="50"
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              />
            </div>
            {form.formState.errors.afspraken && (
              <p className="text-red-500 text-sm">{form.formState.errors.afspraken.message}</p>
            )}
          </div>
          
          <div className="grid gap-3">
            <label htmlFor="doelgroep" className="font-medium text-gray-700 h-7 flex items-center">
              Type doelgroep*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  Selecteer de sector van uw doelgroep. Dit beïnvloedt de complexiteit en prijs van de prospectie.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="doelgroep"
                {...form.register("doelgroep")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="b2b-diensten">B2B Diensten</option>
                <option value="bouw">Bouw</option>
                <option value="retail">Retail</option>
                <option value="anders">Anders</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="grid gap-3">
            <label htmlFor="regio" className="font-medium text-gray-700 h-7 flex items-center">
              Regio*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  Het geografische gebied waarin we voor u prospecteren. Grootschaligere regio's hebben invloed op de prijs.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="regio"
                {...form.register("regio")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="vlaanderen">Vlaanderen</option>
                <option value="heel-belgie">Heel België</option>
              </select>
            </div>
          </div>
          
          <div className="grid gap-3">
            <label htmlFor="crmIntegratie" className="font-medium text-gray-700 h-7 flex items-center">
              CRM-integratie nodig?*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  Geef aan of u integratie wenst met uw bestaande CRM-systeem voor leadopvolging.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="crmIntegratie"
                {...form.register("crmIntegratie")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="nee">Nee</option>
                <option value="ja">Ja (Podio of andere)</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="grid gap-3">
            <label htmlFor="contactpersonen" className="font-medium text-gray-700 h-7 flex items-center">
              Aantal contactpersonen*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  Hoeveel besluitvormers moeten we gemiddeld contacteren om een afspraak te maken?
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="contactpersonen"
                {...form.register("contactpersonen")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="een">1 persoon</option>
                <option value="meerdere">Meerdere personen</option>
              </select>
            </div>
          </div>
          
          <div className="grid gap-3">
            <label htmlFor="taal" className="font-medium text-gray-700 h-7 flex items-center">
              Taal*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  In welke taal moeten we prospecteren? Meertalige campagnes vereisen gespecialiseerde callers.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="taal"
                {...form.register("taal")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="nederlands">Nederlands</option>
                <option value="frans">Frans</option>
                <option value="engels">Engels</option>
                <option value="meertalig">Meertalig</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="grid gap-3">
            <label htmlFor="dmuComplexiteit" className="font-medium text-gray-700 h-7 flex items-center">
              DMU-complexiteit*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  De complexiteit van het besluitvormingsproces bij potentiële klanten. Complexe DMU's vergen meer tijd.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="dmuComplexiteit"
                {...form.register("dmuComplexiteit")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="eenvoudig">Eenvoudig</option>
                <option value="complex">Complex</option>
              </select>
            </div>
          </div>
          
          <div className="grid gap-3">
            <label htmlFor="productComplexiteit" className="font-medium text-gray-700 h-7 flex items-center">
              Complexiteit dienst*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  Hoe complex is uw product of dienst om uit te leggen? Complexere diensten vereisen gespecialiseerde prospectie.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="productComplexiteit"
                {...form.register("productComplexiteit")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="laag">Laag</option>
                <option value="hoog">Hoog</option>
              </select>
            </div>
          </div>
          
          <div className="grid gap-3">
            <label htmlFor="leadsType" className="font-medium text-gray-700 h-7 flex items-center">
              Leads type*
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 cursor-help">
                    <Info className="h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm text-gray-600">
                  Hoe bekend zijn potentiële klanten al met uw bedrijf? Cold leads zijn volledig onbekend, medium hebben enige bekendheid.
                </TooltipContent>
              </Tooltip>
            </label>
            <div className="h-12">
              <select
                id="leadsType"
                {...form.register("leadsType")}
                className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              >
                <option value="cold">Cold</option>
                <option value="medium">Medium</option>
              </select>
            </div>
          </div>
        </div>

        {/* Marktgegevens section */}
        <div className="border-t border-gray-200 pt-5 mt-2">
          <h3 className="font-semibold text-lg mb-6 text-gray-900">Marktgegevens</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="grid gap-5">
              <label htmlFor="marktgrootte" className="font-medium text-gray-800 text-base h-7 flex items-center">
                Marktgrootte (aantal bedrijven)*
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 cursor-help">
                      <Info className="h-4 w-4 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm text-gray-600">
                    Het totale aantal potentiële klanten in uw doelmarkt. Dit bepaalt het maximaal haalbare aantal afspraken.
                  </TooltipContent>
                </Tooltip>
              </label>
              <div className="h-12">
                <input
                  id="marktgrootte"
                  {...form.register("marktgrootte")}
                  type="number"
                  min="1"
                  className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
                />
              </div>
            </div>
            
            <div className="grid gap-5">
              <label htmlFor="conversiePercentage" className="font-medium text-gray-800 text-base h-7 flex items-center">
                Verwacht conversiepercentage (%)*
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 cursor-help">
                      <Info className="h-4 w-4 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm text-gray-600">
                    Het verwachte percentage bedrijven dat een afspraak accepteert. Realistisch ligt dit tussen 1-5%.
                  </TooltipContent>
                </Tooltip>
              </label>
              <div className="h-12">
                <input
                  id="conversiePercentage"
                  {...form.register("conversiePercentage")}
                  type="number"
                  min="0.1"
                  max="100"
                  step="0.1"
                  className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
                />
              </div>
            </div>
            
            <div className="grid gap-5">
              <label htmlFor="campagneDuur" className="font-medium text-gray-800 text-base h-7 flex items-center">
                Campagneduur (maanden)*
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 cursor-help">
                      <Info className="h-4 w-4 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm text-gray-600">
                    Hoeveel maanden wilt u deze prospectiecampagne laten lopen? Dit beïnvloedt de totale marktbewerking.
                  </TooltipContent>
                </Tooltip>
              </label>
              <div className="h-12">
                <input
                  id="campagneDuur"
                  {...form.register("campagneDuur")}
                  type="number"
                  min="1"
                  className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Rendement section */}
        <div className="border-t border-gray-200 pt-5 mt-2">
          <h3 className="font-semibold text-lg mb-6 text-gray-900">Rendement</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="grid gap-5">
              <label htmlFor="offertePercentage" className="font-medium text-gray-800 text-base h-7 flex items-center">
                Verwacht offertepercentage op sales afspraken (%)*
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 cursor-help">
                      <Info className="h-4 w-4 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm text-gray-600">
                    Welk percentage van de gemaakte afspraken leidt tot een offerte? Dit helpt de ROI te berekenen.
                  </TooltipContent>
                </Tooltip>
              </label>
              <div className="relative h-12">
                <input
                  id="offertePercentage"
                  {...form.register("offertePercentage")}
                  type="number"
                  min="1"
                  max="100"
                  className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <Percent size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
            
            <div className="grid gap-5">
              <label htmlFor="dealValue" className="font-medium text-gray-800 text-base h-7 flex items-center">
                Gemiddelde dealwaarde per klant (€)*
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 cursor-help">
                      <Info className="h-4 w-4 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm text-gray-600">
                    De gemiddelde omzet die één nieuwe klant oplevert. Bepaalt de waarde van elke lead.
                  </TooltipContent>
                </Tooltip>
              </label>
              <div className="relative h-12">
                <input
                  id="dealValue"
                  {...form.register("dealValue")}
                  type="number"
                  min="1"
                  className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <EuroIcon size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
            
            <div className="grid gap-5">
              <label htmlFor="closingPercentage" className="font-medium text-gray-800 text-base h-7 flex items-center">
                Gemiddelde closing percentage op offertes (%)*
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 cursor-help">
                      <Info className="h-4 w-4 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm text-gray-600">
                    Welk percentage van uw offertes wordt een deal? Dit is cruciaal voor de ROI-berekening.
                  </TooltipContent>
                </Tooltip>
              </label>
              <div className="relative h-12">
                <input
                  id="closingPercentage"
                  {...form.register("closingPercentage")}
                  type="number"
                  min="1"
                  max="100"
                  className="w-full h-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <Percent size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full h-12 flex justify-center items-center"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Even geduld...
            </>
          ) : (
            <>
              <CalculatorIcon size={20} />
              Bereken jouw prijs
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default CalculatorForm;
